/**
 * Phase 7 Commit J. Stroke icons used across the widget. All SVGs inherit
 * `currentColor` so brand-color theming applies via the parent's color.
 *
 * Sized via the `size` prop (defaults match the tightest visual context
 * each icon is used in). Keep these primitive — no animation, no fill
 * gradients. The widget bundle is icon-light by design; if a step needs
 * a richer mark, prefer a CSS-only treatment (e.g. .sbw-done-mark).
 */

interface IconProps {
  size?: number;
}

export const SearchIcon = ({ size = 16 }: IconProps) => (
  <svg width={size} height={size} viewBox="0 0 16 16" fill="none" stroke="currentColor" stroke-width="1.5" stroke-linecap="round">
    <circle cx="7" cy="7" r="5" />
    <path d="m11 11 3 3" />
  </svg>
);

export const StarIcon = ({ size = 12 }: IconProps) => (
  <svg width={size} height={size} viewBox="0 0 12 12" fill="currentColor">
    <path d="M6 .8 7.5 4.3l3.7.4-2.8 2.6.8 3.7L6 9.1 2.8 11l.8-3.7L.8 4.7l3.7-.4z" />
  </svg>
);

export const CheckIcon = ({ size = 14 }: IconProps) => (
  <svg width={size} height={size} viewBox="0 0 14 14" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
    <path d="M3 7.5 5.5 10l5.5-6" />
  </svg>
);

export const CardIcon = ({ size = 16 }: IconProps) => (
  <svg width={size} height={size} viewBox="0 0 16 16" fill="none" stroke="currentColor" stroke-width="1.4" stroke-linecap="round">
    <rect x="1.5" y="3.5" width="13" height="9" rx="1.5" />
    <path d="M1.5 6.5h13M4 10h3" />
  </svg>
);

export const LockIcon = ({ size = 12 }: IconProps) => (
  <svg width={size} height={size} viewBox="0 0 12 12" fill="none" stroke="currentColor" stroke-width="1.4" stroke-linecap="round">
    <rect x="2.5" y="5.5" width="7" height="5" rx="1" />
    <path d="M4 5.5V4a2 2 0 0 1 4 0v1.5" />
  </svg>
);

export const ArrowIcon = ({ size = 14 }: IconProps) => (
  <svg width={size} height={size} viewBox="0 0 14 14" fill="none" stroke="currentColor" stroke-width="1.6" stroke-linecap="round" stroke-linejoin="round">
    <path d="M2 7h10m-3.5-3.5L12 7l-3.5 3.5" />
  </svg>
);

export const BackIcon = ({ size = 14 }: IconProps) => (
  <svg width={size} height={size} viewBox="0 0 14 14" fill="none" stroke="currentColor" stroke-width="1.6" stroke-linecap="round" stroke-linejoin="round">
    <path d="M12 7H2m3.5-3.5L2 7l3.5 3.5" />
  </svg>
);

export const GlobeIcon = ({ size = 12 }: IconProps) => (
  <svg width={size} height={size} viewBox="0 0 12 12" fill="none" stroke="currentColor" stroke-width="1.2">
    <circle cx="6" cy="6" r="4.5" />
    <path d="M1.5 6h9M6 1.5c1.6 1.5 1.6 7.5 0 9M6 1.5c-1.6 1.5-1.6 7.5 0 9" />
  </svg>
);

export const CalendarIcon = ({ size = 14 }: IconProps) => (
  <svg width={size} height={size} viewBox="0 0 14 14" fill="none" stroke="currentColor" stroke-width="1.4" stroke-linecap="round">
    <rect x="2" y="3" width="10" height="9" rx="1.5" />
    <path d="M5 1.5v3M9 1.5v3M2 6h10" />
  </svg>
);

export const SunIcon = ({ size = 13 }: IconProps) => (
  <svg width={size} height={size} viewBox="0 0 14 14" fill="none" stroke="currentColor" stroke-width="1.3" stroke-linecap="round">
    <circle cx="7" cy="7" r="2.6" />
    <path d="M7 1v1.5M7 11.5V13M1 7h1.5M11.5 7H13M2.7 2.7l1 1M10.3 10.3l1 1M2.7 11.3l1-1M10.3 3.7l1-1" />
  </svg>
);

export const SunsetIcon = ({ size = 13 }: IconProps) => (
  <svg width={size} height={size} viewBox="0 0 14 14" fill="none" stroke="currentColor" stroke-width="1.3" stroke-linecap="round">
    <path d="M7 4v4.5M5 6.5l2-2 2 2M1 11h12M3 8.5h1.5M9.5 8.5H11" />
  </svg>
);

export const MoonIcon = ({ size = 13 }: IconProps) => (
  <svg width={size} height={size} viewBox="0 0 14 14" fill="none" stroke="currentColor" stroke-width="1.3" stroke-linecap="round" stroke-linejoin="round">
    <path d="M11.5 8.5A4.5 4.5 0 0 1 5.5 2.5a5 5 0 1 0 6 6Z" />
  </svg>
);

export const ChevronRightIcon = ({ size = 14 }: IconProps) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5" stroke-linecap="round" stroke-linejoin="round">
    <path d="M9 18l6-6-6-6" />
  </svg>
);
